import type { VoiceNavSessionActivePatientPayload } from '../api/apiConfig'
import type { ChatMessage } from '../types/chat'
import type { PatientContext } from '../types/widget'
import { latestOpenPatientPayloadFromChatMessages } from '../domain/patientHost/activePatientFromChatCore'
import { normalizePatientContext, resolveSportsNocPatientContext } from './sportsNocFlow'

/** Stable id so repeated hydrates replace the same bubble instead of stacking. */
export const SESSION_ACTIVE_PATIENT_DEBUG_MESSAGE_ID = 'voicnav:sessionActivePatientDebug'

function nonEmptyFields(
  obj: Record<string, unknown> | VoiceNavSessionActivePatientPayload | PatientContext | null | undefined,
): string[] {
  if (!obj || typeof obj !== 'object') return []
  const out: string[] = []
  for (const [k, v] of Object.entries(obj)) {
    const s = v == null ? '' : String(v).trim()
    if (s) out.push(`${k}=${JSON.stringify(s)}`)
  }
  return out
}

function formatFields(label: string, fields: string[]): string {
  return fields.length ? `${label}: ${fields.join(', ')}` : `${label}: (none)`
}

function formatSessionPayload(
  label: string,
  p: VoiceNavSessionActivePatientPayload | null | undefined,
): string {
  return formatFields(label, nonEmptyFields(p ?? null))
}

function latestChatPatientHints(messages: readonly ChatMessage[]): {
  nameHint: string
  idHint: string
  noActiveSignal: boolean
  pendingModalityName: string
} {
  let nameHint = ''
  let idHint = ''
  let noActiveSignal = false
  let pendingModalityName = ''
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant') continue
    if (!pendingModalityName) {
      pendingModalityName = (m.pendingModalityExam?.targetPatientName ?? '').trim()
    }
    if (!nameHint) nameHint = (m.activePatientNameHint ?? '').trim()
    if (!idHint) idHint = (m.activePatientIdHint ?? '').trim()
    if (!noActiveSignal && m.noActivePatientContextHint) noActiveSignal = true
    if (pendingModalityName && nameHint && idHint && noActiveSignal) break
  }
  return { nameHint, idHint, noActiveSignal, pendingModalityName }
}

/**
 * TEMP: after every refresh, show what active-patient-related data was loaded from caches
 * (Redis session, host embed, sessionStorage mock reload, chat hints) plus resolved UI context.
 */
export function buildActivePatientHydrateDebugText(input: {
  redisActivePatient: VoiceNavSessionActivePatientPayload | null | undefined
  hostPatientContext: PatientContext | Record<string, unknown> | undefined
  restoredMessages: readonly ChatMessage[]
  sessionStorageMock?: { patientName: string; patientDob?: string } | null
}): string {
  const hostNorm = normalizePatientContext(input.hostPatientContext)
  const hostRaw =
    input.hostPatientContext != null && typeof input.hostPatientContext === 'object'
      ? (input.hostPatientContext as Record<string, unknown>)
      : null
  const chatOpen = latestOpenPatientPayloadFromChatMessages(input.restoredMessages)
  const chatHints = latestChatPatientHints(input.restoredMessages)
  const resolved = resolveSportsNocPatientContext(
    input.hostPatientContext,
    input.restoredMessages,
    input.redisActivePatient ?? null,
  )

  const mock = input.sessionStorageMock
  const mockLine = mock
    ? `sessionStorage mock reload (staged): patientName=${JSON.stringify(mock.patientName)}${
        mock.patientDob ? `, patientDob=${JSON.stringify(mock.patientDob)}` : ''
      }`
    : 'sessionStorage mock reload (staged): (none)'

  const chatHintParts: string[] = []
  if (chatHints.nameHint) chatHintParts.push(`activePatientNameHint=${JSON.stringify(chatHints.nameHint)}`)
  if (chatHints.idHint) chatHintParts.push(`activePatientIdHint=${JSON.stringify(chatHints.idHint)}`)
  if (chatHints.pendingModalityName) {
    chatHintParts.push(`pendingModality.targetPatientName=${JSON.stringify(chatHints.pendingModalityName)}`)
  }
  if (chatHints.noActiveSignal) chatHintParts.push('noActivePatientContextHint=true')

  const lines = [
    '[Active patient — cache snapshot after refresh]',
    formatSessionPayload('Redis active_patient (session/context)', input.redisActivePatient),
    formatFields('Host embed raw (non-empty keys)', nonEmptyFields(hostRaw)),
    formatFields('Host embed normalized', nonEmptyFields(hostNorm ?? null)),
    mockLine,
    formatSessionPayload('Chat open_patient / dossier hints', chatOpen),
    formatFields('Chat assistant hints (latest)', chatHintParts),
    formatFields('Resolved for flows (session → chat; embed ignored)', nonEmptyFields(resolved ?? null)),
  ]
  return lines.join('\n')
}
